/* Phase 4.2.1.5 — Workflow Instance Detail page (route /app/workflows/:id).
 * Reuses the WorkflowTimelineCard pattern from Phase 4.2.1's LP detail —
 * vertical stepper with clickable agent_run_id chips routing to the Agent Run
 * Inspector. Chosen over a drawer for URL-shareability (see ADR-0026).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AuditHistoryLink from "../../components/app/AuditHistoryLink";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ArrowLeft, GitBranch, Sparkles, CheckCircle2, XCircle, MinusCircle, CircleDot } from "lucide-react";
import { SkippedReason, AttemptBadge } from "../../components/app/WorkflowStepDetails";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";

const STATUS_ICON = {
  succeeded: <CheckCircle2 className="size-4 text-emerald-600" />,
  failed: <XCircle className="size-4 text-rose-600" />,
  failed_continue: <XCircle className="size-4 text-amber-600" />,
  skipped: <MinusCircle className="size-4 text-muted-foreground" />,
  running: <CircleDot className="size-4 text-blue-600 animate-pulse" />,
};
const STATUS_TONE = {
  succeeded: "bg-emerald-100 text-emerald-900",
  failed: "bg-rose-100 text-rose-900",
  failed_continue: "bg-amber-100 text-amber-900",
  skipped: "bg-muted text-muted-foreground",
  running: "bg-blue-100 text-blue-900",
};

export default function WorkflowInstanceDetail() {
  const { id } = useParams();
  const t = useT();
  const [inst, setInst] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/workflows/instances/${id}`).then((r) => alive && setInst(r.data))
      .catch(() => alive && setError(true));
    return () => { alive = false; };
  }, [id]);

  if (error) {
    return <div className="text-sm text-muted-foreground" data-testid="wf-instance-error">{t("workflows.history.not_found")}</div>;
  }
  if (!inst) {
    return <div className="text-sm text-muted-foreground" data-testid="wf-instance-loading">{t("common.loading")}</div>;
  }
  const stepRuns = inst.step_runs || [];

  return (
    <div className="space-y-6" data-testid="workflow-instance-page">
      <Link to="/app/workflows" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="wf-instance-back">
        <ArrowLeft className="size-4 mr-1" /> {t("workflows.history.title")}
      </Link>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <GitBranch className="size-5" />
          {inst.definition_key}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="font-mono" data-testid="wf-instance-id-badge">{inst.id.slice(0, 8)}…</Badge>
          <span className={`inline-flex rounded-full px-2 py-0.5 font-mono ${STATUS_TONE[inst.status] || ""}`} data-testid="wf-instance-status">
            {t(`landing_pages.workflow.status.${inst.status}`) || inst.status}
          </span>
          {inst.correlation_id && (
            <Badge variant="outline" className="font-mono">corr: {inst.correlation_id.slice(0, 8)}…</Badge>
          )}
          <AuditHistoryLink kind="workflow_instance" id={inst.id}
                            testid="workflow-audit-history-link" />
        </div>
      </div>
      <Card data-testid="wf-instance-timeline-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><GitBranch className="size-4" />{t("landing_pages.workflow.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="relative border-l-2 border-muted pl-5 space-y-3 ml-1" data-testid="wf-instance-timeline">
            {stepRuns.map((s, i) => {
              const label = t(`landing_pages.workflow.steps.${s.name}`) || s.name;
              const status = s.status || "running";
              const statusLabel = t(`landing_pages.workflow.status.${status}`) || status;
              return (
                <li key={i} className="relative" data-testid={`wf-instance-step-${s.name}`}>
                  <span className="absolute -left-[27px] top-0.5 inline-flex size-5 items-center justify-center rounded-full bg-background ring-2 ring-muted">
                    {STATUS_ICON[status] || STATUS_ICON.running}
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{label}</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono ${STATUS_TONE[status] || ""}`}>{statusLabel}</span>
                    {s.agent_run_id && (
                      <Link to={`/app/agents/runs/${s.agent_run_id}`}
                            data-testid={`wf-instance-step-${s.name}-run-chip`}
                            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-900 px-2 py-0.5 text-[10px] font-mono ring-1 ring-indigo-200">
                        <Sparkles className="size-3" />{(s.agent_run_id || "").slice(0, 8)}…
                      </Link>
                    )}
                    {s.reason && (
                      <SkippedReason step={s} testid={`wf-instance-step-${s.name}`} />
                    )}
                    {(s.attempts && s.attempts.length > 1) && (
                      <AttemptBadge attempts={s.attempts} status={status}
                                    testid={`wf-instance-step-${s.name}-attempts`} />
                    )}
                  </div>
                  {s.error && (
                    <p className="text-[11px] text-rose-700 mt-1 font-mono">{s.error}</p>
                  )}
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>
      <Card data-testid="wf-instance-input-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("workflows.history.input_title")}</CardTitle></CardHeader>
        <CardContent>
          <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-auto max-h-64">{JSON.stringify(inst.input || {}, null, 2)}</pre>
        </CardContent>
      </Card>
    </div>
  );
}
void Button;
